import { createApi } from "@reduxjs/toolkit/dist/query/react"
import { authQuery } from "@store/services/authQuery"

interface SnapshotSaveResponseDto {
  message: string
  commit_sha: string
  url: string
}

interface SnapshotSaveRequestDto {
  key: string
  message: string
}

interface SnapshotRestoreRequestDto {
  key: string
  commit_sha: string
}

interface SnapshotRestoreResponseDto {
  ok: boolean
}

export interface SnapshotDto {
  commit_sha: string
  previous_sha: string
  commit_message: string
  author_name: string
  author_email: string
  date: string
  version: string
  index: number
}

export enum SnapshotFileNameDto {
  DELTA_MESSAGES_BIN = "delta_messages.bin",
  ALL_MESSAGES_BIN = "all_messages.bin",
  GRAPH_BIN = "graph.bin",
  VERSION = ".version",
  INDEX = ".index",
}

interface SnapshotFileRequestDto {
  key: string
  fileName: SnapshotFileNameDto
  commit_sha?: string
}

export const snapshotApi = createApi({
  reducerPath: "snapshotApi",
  baseQuery: authQuery,
  tagTypes: ["Snapshot"],
  endpoints: (builder) => ({
    createSnapshot: builder.mutation<SnapshotSaveResponseDto, SnapshotSaveRequestDto>({
      query: ({ key, message }) => ({
        url: `/snapshots/${encodeURIComponent(key)}?message=${encodeURIComponent(message)}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Snapshot"],
    }),
    restoreSnapshot: builder.mutation<SnapshotRestoreResponseDto, SnapshotRestoreRequestDto>({
      query: ({ key, commit_sha }) => ({
        url: `/snapshots/${encodeURIComponent(key)}/commit/${commit_sha}/restore`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Snapshot"],
    }),
    getSnapshots: builder.query<
      SnapshotDto[],
      {
        key: string
        page?: number
        limit?: number
        author_email?: string
        from_date?: string
        to_date?: string
      }
    >({
      query: ({ key, ...params }) => ({
        url: `/snapshots/${encodeURIComponent(key)}`,
        params,
      }),
      providesTags: ["Snapshot"],
    }),
    getSnapshotFile: builder.query<Uint8Array, SnapshotFileRequestDto>({
      query: ({ key, fileName, commit_sha }) => ({
        url: `/snapshots/${encodeURIComponent(key)}/file/${fileName}${
          commit_sha ? `?commit_sha=${commit_sha}` : ""
        }`,
        responseHandler: async (resp) => {
          if (resp.status !== 200) {
            return resp
          }
          // ms: changed from resp.bytes() to add support for Chromium version < 132 - playwright uses 129
          return new Uint8Array(await resp.arrayBuffer())
        },
      }),
      providesTags: ["Snapshot"],
    }),
  }),
})

export const {
  useCreateSnapshotMutation,
  useRestoreSnapshotMutation,
  useGetSnapshotsQuery,
  useGetSnapshotFileQuery,
  useLazyGetSnapshotFileQuery,
} = snapshotApi
